import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    LayoutDashboard, ShoppingBag, Heart, User, Ticket,
    Gift, Wallet, List, MessageCircle, LogOut, Menu, X, Users,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../api/api';
import { user_reset } from '../store/reducers/authReducer';
import { reset_count } from '../store/reducers/cardReducer';

const NAV_ITEMS = [
    { to: '/dashboard',              end: true, Icon: LayoutDashboard, label: 'Overview' },
    { to: '/dashboard/my-orders',              Icon: ShoppingBag,       label: 'My Orders' },
    { to: '/dashboard/my-wishlist',            Icon: Heart,             label: 'Wishlist' },
    { to: '/dashboard/my-profile',             Icon: User,              label: 'My Profile' },
    { to: '/dashboard/my-coupons',             Icon: Ticket,            label: 'Coupons' },
    { to: '/dashboard/my-rewards',             Icon: Gift,              label: 'Rewards' },
    { to: '/dashboard/my-referrals',           Icon: Users,             label: 'Referrals' },
    { to: '/dashboard/credit-balance',         Icon: Wallet,            label: 'Credit Balance' },
    { to: '/dashboard/my-list',                Icon: List,              label: 'My List' },
    { to: '/dashboard/chat',                   Icon: MessageCircle,     label: 'Chat' },
];

const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-[13px] border-b border-gray-100 last:border-0 transition-colors ${
        isActive
            ? 'text-orange-500 font-semibold bg-orange-50'
            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
    }`;

const Sidebar = ({ userInfo, onLogout }) => (
    <aside className="w-[210px] flex-shrink-0">
        {/* User card */}
        <div className="bg-white rounded-lg mb-2 border border-gray-200 overflow-hidden">
            <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 text-base font-bold flex-shrink-0">
                    {userInfo?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-gray-900 truncate">
                        {userInfo?.name || 'Customer'}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">{userInfo?.email || ''}</p>
                </div>
            </div>

            {/* Nav links */}
            <nav>
                {NAV_ITEMS.map(({ to, end, Icon, label }) => (
                    <NavLink key={to} to={to} end={end} className={navCls}>
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        {label}
                    </NavLink>
                ))}
            </nav>
        </div>

        {/* Logout */}
        <button
            onClick={onLogout}
            className="w-full bg-white rounded-lg border border-gray-200 px-4 py-3 text-[13px] text-gray-600 hover:text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors"
        >
            <LogOut className="w-4 h-4" />
            Sign Out
        </button>
    </aside>
);

const Dashboard = () => {
    const [mobileNav, setMobileNav] = useState(false);
    const navigate  = useNavigate();
    const dispatch  = useDispatch();
    const { userInfo } = useSelector(state => state.auth);

    const logout = async () => {
        try {
            await api.get('/customer/logout');
            localStorage.removeItem('customerToken');
            dispatch(user_reset());
            dispatch(reset_count());
            navigate('/customer-auth');
        } catch (error) {
            console.log(error.response?.data);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-[#f5f5f5]">
            <Header />

            {/* Mobile sidebar toggle */}
            <div className="mds:hidden bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-3">
                <button
                    onClick={() => setMobileNav(!mobileNav)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700"
                >
                    {mobileNav ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                    My Account
                </button>
            </div>

            <div className="flex-1 py-4">
                <div className="max-w-[1180px] mx-auto px-4">
                    <div className="flex gap-3 items-start">

                        {/* Desktop sidebar */}
                        <div className="hidden mds:block">
                            <Sidebar userInfo={userInfo} onLogout={logout} />
                        </div>

                        {/* Mobile sidebar dropdown */}
                        {mobileNav && (
                            <div className="mds:hidden fixed inset-0 z-40">
                                <div
                                    className="absolute inset-0 bg-black/30"
                                    onClick={() => setMobileNav(false)}
                                />
                                <div className="absolute top-[110px] left-0 right-0 bg-[#f5f5f5] px-4 pt-3 pb-4 z-50 shadow-lg border-t border-gray-200">
                                    <Sidebar userInfo={userInfo} onLogout={() => { setMobileNav(false); logout(); }} />
                                </div>
                            </div>
                        )}

                        {/* Page content */}
                        <main className="flex-1 min-w-0">
                            <Outlet />
                        </main>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Dashboard;
